const IMAGE_MIME_RE = /^image\/(jpeg|jpg|png|webp|gif|heic|heif)$/i;
const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|heic|heif)$/i;
const GOOGLE_PHOTOS_RE = /photos\.google\.com|photos\.app\.goo\.gl|googleusercontent\.com/i;
const MAX_FILES = 30;
const MAX_BYTES = 12 * 1024 * 1024;

function isHeicFile(file) {
  const type = String(file && file.type || '').toLowerCase();
  const name = String(file && file.name || '').toLowerCase();
  return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
}

function isAcceptedImageFile(file) {
  if (!file) return false;
  if (file.type && IMAGE_MIME_RE.test(file.type)) return true;
  return IMAGE_EXT_RE.test(String(file.name || ''));
}

function mimeForFile(file) {
  const type = String(file && file.type || '').toLowerCase();
  if (type === 'image/jpg') return 'image/jpeg';
  if (type && IMAGE_MIME_RE.test(type) && !isHeicFile(file)) return type;
  const name = String(file && file.name || '').toLowerCase();
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.webp')) return 'image/webp';
  if (name.endsWith('.gif')) return 'image/gif';
  return 'image/jpeg';
}

function filesFromList(list) {
  const unique = [];
  const seen = new Set();
  for (const file of Array.from(list || [])) {
    if (!isAcceptedImageFile(file)) continue;
    const key = `${file.name || ''}:${file.size || 0}:${file.lastModified || 0}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(file);
    if (unique.length >= MAX_FILES) break;
  }
  return unique;
}

function extractImageUrlsFromHtml(html) {
  const urls = [];
  const seen = new Set();
  const push = (raw) => {
    const src = String(raw || '').trim().replace(/[),;.\]]+$/g, '');
    if (!src || seen.has(src)) return;
    if (!/^https?:\/\//i.test(src)) return;
    seen.add(src);
    urls.push(src);
  };
  const imgRe = /<img\b[^>]*\bsrc=["']([^"']+)["']/gi;
  let match;
  while ((match = imgRe.exec(String(html || '')))) push(match[1]);
  const gRe = /https:\/\/lh[3-6]\.googleusercontent\.com\/[^\s"'<>]+/gi;
  while ((match = gRe.exec(String(html || '')))) push(match[0]);
  return urls;
}

function urlsIfOnlyLinks(text) {
  const parts = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length || parts.some((part) => !/^https?:\/\//i.test(part))) return [];
  return parts.map((part) => part.replace(/[),;.\]]+$/g, ''));
}

function isGooglePhotosOrImageUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  if (GOOGLE_PHOTOS_RE.test(raw)) return true;
  return /\.(jpe?g|png|webp|gif)(\?|$)/i.test(raw);
}

function collectClipboardPayload(clipboardData) {
  const files = [];
  const urls = [];
  if (!clipboardData) return { files: [], urls: [] };

  const items = Array.from(clipboardData.items || []);
  for (const item of items) {
    if (item.type && item.type.startsWith('image/') && typeof item.getAsFile === 'function') {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  files.push(...filesFromList(clipboardData.files));

  const html = typeof clipboardData.getData === 'function' ? clipboardData.getData('text/html') : '';
  if (html) urls.push(...extractImageUrlsFromHtml(html));

  const text = typeof clipboardData.getData === 'function' ? clipboardData.getData('text/plain') : '';
  if (text) {
    urls.push(...urlsIfOnlyLinks(text).filter(isGooglePhotosOrImageUrl));
  }

  return {
    files: filesFromList(files),
    urls: [...new Set(urls.filter(isGooglePhotosOrImageUrl))],
  };
}

function filesFromDrop(dataTransfer) {
  if (!dataTransfer) return { files: [], urls: [] };
  const files = filesFromList(dataTransfer.files);
  const text = typeof dataTransfer.getData === 'function' ? dataTransfer.getData('text/plain') : '';
  const html = typeof dataTransfer.getData === 'function' ? dataTransfer.getData('text/html') : '';
  const urls = [
    ...extractImageUrlsFromHtml(html),
    ...urlsIfOnlyLinks(text),
  ].filter(isGooglePhotosOrImageUrl);
  return { files, urls: [...new Set(urls)] };
}

function heicWarning(count) {
  if (!count) return '';
  const n = count === 1 ? 'una foto HEIC' : `${count} fotos HEIC`;
  return `Se omitió ${n}: Google Fotos a veces comparte en HEIC. Vuelve a compartirlas como JPG («Enviar una copia» o «Archivo») o elige las fotos desde el selector.`;
}

module.exports = {
  MAX_FILES,
  MAX_BYTES,
  isHeicFile,
  isAcceptedImageFile,
  mimeForFile,
  filesFromList,
  extractImageUrlsFromHtml,
  urlsIfOnlyLinks,
  isGooglePhotosOrImageUrl,
  collectClipboardPayload,
  filesFromDrop,
  heicWarning,
};
